// Settings → Notifications (Wave E).
//
// Subset of the company-settings reminders tab, lifted to its own route so
// staff can find it directly. Toggles + per-channel default lead-time and
// time-of-day. Backed by PATCH /api/v1/company.

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Save } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useCompanyOverview, useUpdateCompany } from '../company/hooks/useCompany';
import type { UpdateCompanyInput } from '../../../types/api';
import styles from './NotificationSettingsPage.module.css';

interface NotifForm {
  email_enabled: boolean;
  email_hours: number;
  email_time: string;
  sms_enabled: boolean;
  sms_hours: number;
  sms_time: string;
}

export function NotificationSettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const overview = useCompanyOverview();
  const update = useUpdateCompany();

  const [form, setForm] = useState<NotifForm>({
    email_enabled: false,
    email_hours: 24,
    email_time: '09:00',
    sms_enabled: false,
    sms_hours: 24,
    sms_time: '09:00',
  });

  useEffect(() => {
    if (!overview.data) return;
    const c = overview.data.company;
    setForm({
      email_enabled: c.automated_email_reminder_enabled,
      email_hours: c.automated_email_reminder_hours,
      email_time: c.automated_email_reminder_time,
      sms_enabled: c.automated_sms_reminder_enabled,
      sms_hours: c.automated_sms_reminder_hours,
      sms_time: c.automated_sms_reminder_time,
    });
  }, [overview.data]);

  if (overview.isLoading || !overview.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="Notifications" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  function set<K extends keyof NotifForm>(key: K, value: NotifForm[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: UpdateCompanyInput = {
      automated_email_reminder_enabled: form.email_enabled,
      automated_email_reminder_hours: form.email_hours,
      automated_email_reminder_time: form.email_time,
      automated_sms_reminder_enabled: form.sms_enabled,
      automated_sms_reminder_hours: form.sms_hours,
      automated_sms_reminder_time: form.sms_time,
    };
    update.mutate(payload);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Notification Settings"
        subtitle="Manage automated email and SMS reminders sent to customers"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings')}
          >
            Back
          </Button>
        }
      />

      <Card title="Automated Reminders">
        <form onSubmit={handleSubmit} className={styles.form}>
          <section className={styles.section}>
            <header className={styles.sectionHead}>
              <Bell size={16} aria-hidden />
              <h3>Email Reminder</h3>
            </header>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={form.email_enabled}
                onChange={(e) => set('email_enabled', e.target.checked)}
              />
              <span>Send email reminder before collection</span>
            </label>
            <div className={styles.row}>
              <Input
                label="Hours Before"
                variant="number"
                min={1}
                max={720}
                value={form.email_hours}
                onChange={(e) => set('email_hours', Number(e.target.value))}
                disabled={!form.email_enabled}
              />
              <Input
                label="Time of Day"
                value={form.email_time}
                onChange={(e) => set('email_time', e.target.value)}
                helper="HH:MM 24-hour"
                disabled={!form.email_enabled}
              />
            </div>
          </section>

          <section className={styles.section}>
            <header className={styles.sectionHead}>
              <Bell size={16} aria-hidden />
              <h3>SMS Reminder</h3>
            </header>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={form.sms_enabled}
                onChange={(e) => set('sms_enabled', e.target.checked)}
              />
              <span>Send SMS reminder before collection</span>
            </label>
            <div className={styles.row}>
              <Input
                label="Hours Before"
                variant="number"
                min={1}
                max={720}
                value={form.sms_hours}
                onChange={(e) => set('sms_hours', Number(e.target.value))}
                disabled={!form.sms_enabled}
              />
              <Input
                label="Time of Day"
                value={form.sms_time}
                onChange={(e) => set('sms_time', e.target.value)}
                helper="HH:MM 24-hour"
                disabled={!form.sms_enabled}
              />
            </div>
          </section>

          <div className={styles.footer}>
            <Button type="submit" variant="primary" icon={Save} loading={update.isPending}>
              Save Notification Settings
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
